import type {
  AxiosError,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import type { ApiConfig, BaseAuthState } from "./types";

let isRefreshing = false;
let isRedirecting = false;
let failedQueue: {
  resolve: (value?: AxiosResponse) => void;
  reject: (error: unknown) => void;
  config: InternalAxiosRequestConfig;
}[] = [];

const processQueue = (
  error: unknown,
  token: string | null,
  ApiClient: AxiosInstance,
) => {
  failedQueue.forEach(async ({ resolve, reject, config }) => {
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      resolve(await ApiClient(config));
    } else {
      reject(error);
    }
  });
  failedQueue = [];
};

interface JwtPayloadType {
  exp?: number;
  iat?: number;
}

function parseJwt(token: string): JwtPayloadType | null {
  try {
    const base64Url = token.split(".")[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map(function (c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join("")
    );
    return JSON.parse(jsonPayload) as JwtPayloadType;
  } catch (e) {
    return null;
  }
}

// Track stores that have already initialized the proactive refresh to avoid duplicate timers
const initializedStores = new WeakSet();

export const setupInterceptors = <TAuthState extends BaseAuthState>(
  ApiClient: AxiosInstance,
  AuthClient: AxiosInstance,
  config: ApiConfig<TAuthState>,
) => {
  const { store, persistor, routes, endpoints, onAuthUpdate, onAuthClear } =
    config;

  const executeRefresh = async (): Promise<string> => {
    isRefreshing = true;

    try {
      const state = store.getState();

      if (state.auth.is_impersonation) {
        throw new Error("Impersonation sessions cannot be refreshed");
      }

      const response = await AuthClient.post(endpoints.REFRESH_TOKEN, {
        refresh: state.auth.refresh,
      });

      const tokens = response.data.data;
      onAuthUpdate(tokens);

      processQueue(null, tokens.access, ApiClient);

      return tokens.access;
    } catch (err) {
      processQueue(err, null, ApiClient);

      const state = store.getState();

      if (state.auth.is_impersonation) {
        console.error("Impersonation session failed to refresh. Ignoring auto-logout.");
        return Promise.reject(err);
      }

      await persistor.purge();

      if (!isRedirecting) {
        isRedirecting = true;
        onAuthClear();
        window.location.href = state.auth.onboarding_token
          ? routes.PRICING
          : routes.LOGIN;
      }

      return Promise.reject(err);
    } finally {
      isRefreshing = false;
    }
  };

  if (!initializedStores.has(store)) {
    initializedStores.add(store);

    const initProactiveRefresh = () => {
      let currentToken: string | null = null;
      let refreshTimer: ReturnType<typeof setTimeout> | null = null;

      const checkAndScheduleRefresh = () => {
      const state = store.getState();
      const token = state.auth?.access || null;

      if (token !== currentToken) {
        currentToken = token;
        
        if (refreshTimer) {
          clearTimeout(refreshTimer);
          refreshTimer = null;
        }

        if (token) {
          const payload = parseJwt(token);
          if (payload && payload.exp) {
            const expiresAt = payload.exp * 1000;
            const remainingTime = expiresAt - Date.now();

            // Calculate total lifespan to make the refresh completely dynamic
            let totalLifespan = remainingTime;
            if (payload.iat) {
              const issuedAt = payload.iat * 1000;
              totalLifespan = expiresAt - issuedAt;
            }

            // We want to wait for a certain percentage of the token's total lifespan, 
            // Default is 0.8 (80%). Change this variable if you want to refresh earlier or later.
            const REFRESH_THRESHOLD_PERCENTAGE = 0.8;
            let refreshDelay = totalLifespan * REFRESH_THRESHOLD_PERCENTAGE;

            // If the token is being loaded from local storage and is already partially used,
            // the calculated refreshDelay might be in the past or very soon. 
            // We ensure we don't wait longer than the actual remaining time minus a small buffer (min 15 seconds)
            if (refreshDelay > remainingTime - 15000) {
                refreshDelay = remainingTime - 15000;
            }

            // If remainingTime <= 15 seconds, we do NOT set a timer. 
            // We let the token expire naturally and rely on the 401 fallback to prevent loops.
            if (refreshDelay > 0) {
              refreshTimer = setTimeout(() => {
                if (!isRefreshing) {
                  executeRefresh().catch((err) => {
                    console.error("Proactive token refresh failed", err);
                  });
                }
              }, refreshDelay);
            }
          }
        }
      }
      };

      // Initial check in case state is already populated (e.g. Redux Persist rehydration)
      checkAndScheduleRefresh();

      // Subscribe to future token changes
      store.subscribe(() => {
        checkAndScheduleRefresh();
      });
    };

    // To turn ON or OFF the proactive token refresh feature, simply uncomment/comment the line below:
    initProactiveRefresh();
  }

  // REQUEST
  ApiClient.interceptors.request.use((request: InternalAxiosRequestConfig) => {
    const state = store.getState();
    const token = state.auth;

    if (token?.access || token?.onboarding_token) {
      request.headers.Authorization = `Bearer ${
        token.access ?? token.onboarding_token
      }`;
    }
    return request;
  });

  // RESPONSE
  ApiClient.interceptors.response.use(
    (res: AxiosResponse) => res,
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & {
        _retry?: boolean;
      };

      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        if (isRefreshing) {
          return new Promise((resolve, reject) =>
            failedQueue.push({ resolve, reject, config: originalRequest }),
          );
        }

        try {
          const newAccessToken = await executeRefresh();
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return ApiClient(originalRequest);
        } catch (err) {
          return Promise.reject(err);
        }
      }

      if (
        error.response?.data instanceof Blob &&
        error.response.data.type === "application/json"
      ) {
        try {
          const text = await error.response.data.text();
          error.response.data = JSON.parse(text);
        } catch (e) {
          console.error("Failed to parse blob to JSON in interceptor", e);
        }
      }

      if (
        error.response?.status === 404 &&
        ((error.response?.data as any)?.detail === "Invalid page." ||
          (error.response?.data as any)?.message === "Invalid page.")
      ) {
        console.warn(
          `[DRF Pagination Error] "Invalid page." returned from URL: ${error.config?.url} | Params:`,
          error.config?.params,
        );
      }

      return Promise.reject(error);
    },
  );
};
